import _ from 'lodash';
import { share, take } from 'rxjs/operators';
// import * as constants from '../constants';

// const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
// const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').ChatMessage} ChatMessage
 * @typedef {import('./state').ChatRoom} ChatRoom
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} WatchChatRoomsActionOpts
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * @callback WatchChatRoomsAction
 * @param {ActionContext} ctx
 * @param {WatchChatRoomsActionOpts} opts
 * @returns {Promise<ChatRoom[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {WatchChatRoomsAction}
 */
export const watchChatRooms = (store, sdk) => async (ctx, opts) => {
  const currentUser = await sdk.auth().currentUser();
  // reset store
  ctx.commit('setRoomsSub', null);

  // process stream
  const query = {};
  const stream = sdk.chat.rooms().find(query).watch({
    timeoutInterval: 5000,
    emitChangedItem: true
  }).pipe(share());
  const sub = stream.subscribe(rooms => rooms.forEach(room => {
    room.participants = room.participantsPopulated;
    switch (room.__op) {
      case 'create':
      case 'update':
      default : return ctx.commit('updateInRooms', {
        upsert: true,
        item: room,
        currentUser: currentUser.uid
      });
    }
  }));
  ctx.commit('setRoomsSub', sub);

  // return first emit
  return stream.pipe(take(1)).toPromise();
};

/**
 * @typedef {Object} WatchChatMessagesActionOpts
 * @property {string} [room] room id
 * @property {string[]} [participants] for creating new room
 * @property {boolean} $forceFetch - use only for refresh buttons
 * @property {boolean} $markRoomAsSeen - mark all the messages in the loaded room as seen
 *
 * @callback WatchChatMessagesAction
 * @param {ActionContext} ctx
 * @param {WatchChatMessagesActionOpts} opts
 * @returns {Promise<ChatRoom>} created/declared room
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {WatchChatMessagesAction}
 */
export const watchChatMessages = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ $markRoomAsSeen: true }, opts);

  const currentUser = await sdk.auth().currentUser();
  if (!opts.room) {
    if (!opts.participants || !opts.participants.length) throw new Error(`Participants required to create new room`);
    const room = await sdk.chat.rooms().create(_.pick(opts, 'participants'));
    ctx.commit('updateInRooms', {
      upsert: true,
      item: room,
      currentUser: currentUser.uid
    });
    opts.room = room.id;
  }
  if (!opts.room) throw new Error(`Room required`);

  const messageProcessor = message => {
    switch (message.__op) {
      case 'create':
      case 'update':
      default: return ctx.commit('updateInMessages', {
        upsert: true,
        item: message
      });
    }
  };
  const markRoomAsSeen = async mesages => {
    // mark room as seen
    const room = await sdk.chat.rooms()
      .update(opts.room, { seen: true })
      .catch(error => console.error(error));
    ctx.commit('updateInRooms', {
      item: room,
      currentUser: currentUser.uid
    });
    return room;
  };

  // already have subscription, refetch messages
  if (ctx.state.messagesSub) {
    // TODO: limit to only later than the latest in cache
    // const lastMessage = _.sortBy(ctx.state.chatMessages.filter(m => m.room === obj.room));
    const query = { room: opts.room, $limit: 50, $sort: { createdAt: -1 } };
    const messages = await sdk.chat.messages().find(query).then(r => r.items);
    messages.forEach(messageProcessor);
    return markRoomAsSeen(messages);
  }

  // process stream
  const query = { room: opts.room, $limit: 50, $sort: { createdAt: -1 } };
  const stream = sdk.chat.messages().find(query).watch({
    timeoutInterval: 1000,
    emitChangedItem: true
  }).pipe(share());
  const sub = stream.subscribe(messages => messages.forEach(messageProcessor));
  ctx.commit('setMessagesSub', sub);

  // return first emit
  return stream.pipe(take(1)).toPromise().then(markRoomAsSeen);
};

/**
 * @typedef {Object} CreateChatMessageActionOpts
 * @property {string} room
 * @property {Object} text
 * @property {Object} media
 *
 * @callback CreateChatMessageAction
 * @param {ActionContext} ctx
 * @param {CreateChatMessageActionOpts} opts
 * @returns {Promise<ChatMessage>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateChatMessageAction}
 */
export const createChatMessage = (store, sdk) => async (ctx, opts) => {
  if (!opts) throw new Error(`Message requried`);
  if (!opts.room) throw new Error(`Room is require`);
  if (!opts.text && !opts.media) throw new Error('Text or media URL required');

  // optimisitc sending
  if (opts.failed) delete opts.failed;
  ctx.commit('updateInMessages', { item: opts, equalityCheck: true });

  // actual sending
  const message = await sdk.chat.messages().create(opts)
    // mark message as failed send
    .catch(error => {
      opts.failed = true;
      ctx.commit('updateInMessages', { upsert: true, item: opts, equalityCheck: true });
      throw error;
    });

  // remove optimistic message data
  ctx.commit('removeInMessages', { item: opts, equalityCheck: true });

  return message;
};
